/**
 * 业务列表页 (S3-S8)：合同/发票/收款/邮寄/资料/订单
 * 都基于 BizListPage，按 v7 demo 设计：stat-cards + filter-bar + ck-table
 */
import { useMemo, useState, ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
// ux4：列表筛选/页码/tab 进 URL
import { useSearchParams } from 'react-router-dom';
import { Modal, Input, Select } from 'antd';
import dayjs from 'dayjs';
import { $ } from '../../lib/api';
import {
  PageHeader, StatsRow, StatCard, FilterBar, TableWrap, TableFooter,
  Btn, Pill, PillTone, CustCell, avatarSlot, invTypeLabel,
} from '../../components/ui';
// ux9：日期展示统一出口
import { fmtDate, fmtDateShort } from '../../lib/format';
import { JourneyModal } from '../../components/JourneyModal';
import { toast } from '../../lib/toast';
// v1.5 C-1.b #2：录入回款弹窗
import { PaymentRecordModal } from '../../components/PaymentRecordModal';
// 项6：邮寄列表「+新建邮寄单」复用客户详情页的邮寄建单弹窗
import { ShipmentCreateModal } from '../../components/BizCreateModals';
// v1.5 批1 #2：按 role 显示按钮
import { useAuthStore } from '../../store/auth';
// P1-3b 删除/作废按钮权限位（仅 admin/director 持 contract:delete/invoice:void/... 显示）
import { useHasPermission } from '../../lib/permissions';

interface BizColumn {
  title: string;
  width?: string;
  /** Renders the cell. The optional 2nd/3rd args expose the row index and full page items
   *  for cells that need cross-row context (e.g. "is this a renewal contract?"). */
  render: (row: any, idx?: number, allRows?: any[]) => ReactNode;
  align?: 'left' | 'right';
}

interface BizListConfig {
  endpoint: string;
  pageTitle: string;
  pageTag: string;
  pageSubtitle: string;
  breadcrumb: string[];
  primaryAction?: { label: string; onClick: () => void };
  /** A list of filter tab keys, e.g. [{ key: 'all', label: '全部' }, ...]. The key 'all' = no filter. */
  filterTabs: Array<{ key: string; label: string; param?: Record<string, any> }>;
  /** Compute stat-card data from the current page; if backend doesn't expose, fall back to total. */
  stats: (items: any[], total: number) => Array<{
    label: string; value: ReactNode; unit?: string; meta?: ReactNode; valueColor?: 'warning' | 'success' | 'danger';
  }>;
  columns: BizColumn[];
  /** Optional extra row class (e.g. "expire-soon" for contracts within 30 days). */
  rowClassName?: (row: any) => string | undefined;
}

// ux9：日期展示统一走 '@/lib/format' 出口（fmtDate/fmtDateShort 已内置空值返 '-'）
function date(t?: string) { return fmtDate(t); }
function dateShort(t?: string) { return fmtDateShort(t); }
function money(v: any) {
  if (v === null || v === undefined || Number(v) === 0) return <span className="money muted">—</span>;
  return <span className="money">¥{Number(v).toLocaleString()}</span>;
}
function moneyBig(v: any) {
  if (v === null || v === undefined || Number(v) === 0) return <div className="money muted">—</div>;
  return <div className="money large">¥ {Number(v).toLocaleString()}</div>;
}
/** Double-line money cell: big number + a sub line (e.g. "全款 · 5月12日"). */
function moneyStack({ value, sub, tone }: { value: any; sub?: string; tone?: 'success' | 'warning' | 'danger' | 'muted' }) {
  if (value === null || value === undefined || Number(value) === 0) {
    return <div className="money muted">—</div>;
  }
  const color = tone === 'success' ? 'var(--success-text)'
    : tone === 'warning' ? 'var(--warning-text)'
    : tone === 'danger' ? 'var(--danger-text)'
    : undefined;
  return (
    <>
      <div className="money" style={color ? { color } : undefined}>¥ {Number(value).toLocaleString()}</div>
      {sub && <div className="money-sub">{sub}</div>}
    </>
  );
}
/** Single check-pip with ✓ SVG (matches v7 demo). */
function CheckPip({ label, missing }: { label: string; missing?: boolean }) {
  return (
    <span className={`check-pip ${missing ? 'miss' : ''}`}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
        {missing
          ? <path d="M18 6 6 18M6 6l12 12" />
          : <path d="M5 13l4 4L19 7" />}
      </svg>
      {label}
    </span>
  );
}
/** approval-flow done → current → pending (matches v7 demo s8). */
function ApprovalFlow({ status }: { status: string }) {
  const states: Array<'done' | 'current' | 'pending'> = (() => {
    if (status === 'approved') return ['done', 'done', 'done'];
    if (status === 'pending' || status === 'submitted') return ['done', 'current', 'pending']; // 评审#H26：订单审批态是 pending
    if (status === 'rejected') return ['done', 'done', 'pending'];
    return ['pending', 'pending', 'pending'];
  })();
  const labels = ['已送审', '主管', '财务'];
  return (
    <div className="approval-flow">
      {labels.map((l, i) => (
        <span key={l} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {i > 0 && <span className="flow-arrow">→</span>}
          <span className={`flow-step ${states[i]}`}>
            <span className="flow-step-dot" />
            {l}
          </span>
        </span>
      ))}
    </div>
  );
}

function BizListPage({ cfg }: { cfg: BizListConfig }) {
  // ux4：tab/page 进 URL，刷新/后退不丢（每页独立路由，通用键 tab/page 不串值）
  const [searchParams, setSearchParams] = useSearchParams();
  const tabKeys = cfg.filterTabs.map((t) => t.key);
  const [tab, setTab] = useState(() => {
    const t = searchParams.get('tab');
    return t && tabKeys.includes(t) ? t : cfg.filterTabs[0].key;
  });
  const [page, setPage] = useState(() => { const p = Number(searchParams.get('page')); return p >= 1 ? p : 1; });
  const [journeyCid, setJourneyCid] = useState<number | null>(null);
  const pageSize = 20;
  const tabCfg = cfg.filterTabs.find((t) => t.key === tab) ?? cfg.filterTabs[0];

  // 列表态写回 URL（替换式、不堆历史中间态；默认值省略键）
  const syncUrl = (next: { tab?: string; page?: number }) => {
    const sp = new URLSearchParams(searchParams);
    const t = next.tab ?? tab;
    const pg = next.page ?? page;
    t === cfg.filterTabs[0].key ? sp.delete('tab') : sp.set('tab', t);
    pg <= 1 ? sp.delete('page') : sp.set('page', String(pg));
    setSearchParams(sp, { replace: true });
  };


  const params = useMemo(
    () => ({ page, pageSize, ...(tabCfg.param ?? {}) }),
    [page, pageSize, tabCfg],
  );

  const { data, isLoading } = useQuery({
    queryKey: [cfg.endpoint, params],
    queryFn: () => $.get<any>(cfg.endpoint, params),
  });

  const items: any[] = data?.items ?? data ?? [];
  const total: number = data?.total ?? items.length;
  const stats = cfg.stats(items, total);

  /** Export current filter as CSV (UTF-8 BOM for Excel). Falls back to fields by title→key best-effort. */
  const exportCsv = () => {
    if (items.length === 0) { toast.warn('当前页无数据可导出'); return; }
    const cols = cfg.columns.filter((c) => c.title !== '操作');
    const header = cols.map((c) => c.title).join(',');
    const body = items.map((r) => cols.map((c) => {
      const key = c.title;
      // Best-effort: look up by lowercase field name from row
      const lookup =
        r[(key as string).toLowerCase()] ??
        r.contractCode ?? r.invoiceCode ?? r.paymentCode ?? r.shipmentCode ?? r.materialCode ?? r.orderCode ?? '';
      const fallback = c.title === '客户' ? `客户#${r.customerId}`
        : c.title.includes('金额') || c.title === '应收' || c.title === '已收' || c.title === '尾款' ? (r.amount ?? r.totalDue ?? r.totalPaid ?? r.balanceDue ?? '')
        : c.title === '状态' ? (r.status ?? r.approvalStatus ?? r.submitStatus ?? r.esignStatus ?? '')
        : c.title === '编号' || c.title === '合同编号' ? lookup
        : c.title === '类型' ? (r.type ?? r.orderType ?? '')
        : c.title === '提成' ? (r.commission ?? '')
        : c.title === '审批' || c.title === '审批状态' ? (r.approvalStatus ?? '')
        : c.title === '创建时间' || c.title === '开票日期' || c.title === '提交时间' ? dayjs(r.createdAt).format('YYYY-MM-DD')
        : lookup;
      const s = String(fallback ?? '').replace(/"/g, '""');
      return `"${s}"`;
    }).join(',')).join('\n');
    const csv = '﻿' + header + '\n' + body;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${cfg.pageTitle}-${dayjs().format('YYYYMMDD-HHmm')}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast.success(`已导出 ${items.length} 条到 CSV`);
  };

  // Counts per tab — kick off in parallel (lightweight; ignore errors)
  const tabCounts = useQuery({
    // 存量bug2：key 含 tab 列表——权限晚加载致 filterTabs 后补「作废」tab 时,计数能随之重算(否则作废tab角标缺数字)
    queryKey: [cfg.endpoint, 'tab-counts', cfg.filterTabs.map((t) => t.key).join(',')],
    queryFn: async () => {
      const counts: Record<string, number> = {};
      for (const t of cfg.filterTabs) {
        try {
          const r = await $.get<any>(cfg.endpoint, { page: 1, pageSize: 1, ...(t.param ?? {}) });
          counts[t.key] = r.total ?? (Array.isArray(r) ? r.length : 0);
        } catch { counts[t.key] = 0; }
      }
      return counts;
    },
  });

  return (
    <>
      <PageHeader
        breadcrumb={cfg.breadcrumb}
        title={cfg.pageTitle}
        tag={cfg.pageTag}
        subtitle={cfg.pageSubtitle}
        actions={
          <>
            <Btn kind="text" onClick={exportCsv}>导出</Btn>
            {cfg.primaryAction && (
              <Btn kind="primary" onClick={cfg.primaryAction.onClick}>{cfg.primaryAction.label}</Btn>
            )}
          </>
        }
      />
      <div className="work-content">
        <StatsRow>
          {stats.map((s, i) => (
            <StatCard
              key={i}
              label={s.label}
              value={s.value}
              unit={s.unit}
              meta={s.meta}
              valueColor={s.valueColor}
            />
          ))}
        </StatsRow>

        <FilterBar
          active={tab}
          onChange={(k) => { setTab(k); setPage(1); syncUrl({ tab: k, page: 1 }); }}
          tabs={cfg.filterTabs.map((t) => ({
            key: t.key,
            label: t.label,
            count: tabCounts.data?.[t.key],
          }))}
        />

        <TableWrap>
          <table className="ck-table">
            <thead>
              <tr>
                {cfg.columns.map((c, i) => (
                  <th key={i} style={{ width: c.width, textAlign: c.align ?? 'left' }}>{c.title}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={cfg.columns.length} style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>加载中…</td></tr>
              )}
              {!isLoading && items.length === 0 && (
                <tr><td colSpan={cfg.columns.length} style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>暂无数据</td></tr>
              )}
              {items.map((row, idx) => {
                // First row on the "all" tab acts as the journey example — purple highlight + 查看旅程 action
                const isJourneyRow = idx === 0 && tab === cfg.filterTabs[0].key && row.customerId;
                const extraCls = cfg.rowClassName?.(row);
                const cls = [isJourneyRow ? 'journey-row' : '', extraCls].filter(Boolean).join(' ');
                return (
                  <tr
                    key={row.id}
                    className={cls}
                    onClick={() => isJourneyRow && setJourneyCid(row.customerId)}
                    style={isJourneyRow ? { cursor: 'pointer' } : undefined}
                  >
                    {cfg.columns.map((c, i) => (
                      // v1.5 真修 #2：原 isJourneyRow hijack 整个 td 替换为"查看旅程"按钮 →
                      //   把 cfg.columns 操作列的真实按钮（录入回款 / 合同审批 / 发票开票…）全部吞掉。
                      //   现：所有 column 走正常 cfg.render；journey 体验靠 tr.journey-row 紫高亮 + 行 onClick(line 246)
                      <td key={i} style={{ textAlign: c.align ?? 'left' }}>{c.render(row, idx, items)}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {total > 0 && (
            <TableFooter total={total} page={page} pageSize={pageSize} onPage={(p) => { setPage(p); syncUrl({ page: p }); }} />
          )}
        </TableWrap>
      </div>
      <JourneyModal open={!!journeyCid} customerId={journeyCid} onClose={() => setJourneyCid(null)} />
    </>
  );
}

/* ─── Status pill helpers ───────────────────────────────── */
function statusPill(s: string, map: Record<string, { tone: PillTone; label: string }>): ReactNode {
  const m = map[s];
  if (!m) return <Pill tone="default">{s ?? '—'}</Pill>;
  return <Pill tone={m.tone}>{m.label}</Pill>;
}

/* ─── S3: 合同 ──────────────────────────────────────────── */
// Real esign_status values per DB migration: draft / sent / customer_signed / all_signed.
// "已签订" tab keys are 'signed' or 'all_signed' (UI uses the friendly label).
const CONTRACT_STATUS: Record<string, { tone: PillTone; label: string }> = {
  draft: { tone: 'default', label: '草稿' },
  sent: { tone: 'warning', label: '待签字' },
  customer_signed: { tone: 'primary', label: '客户已签' },
  all_signed: { tone: 'success', label: '已签订' },
  signed: { tone: 'success', label: '已签订' }, // legacy alias
  cancelled: { tone: 'default', label: '已取消' },
};
/** 合同「标记已签」按钮（C批·项3 后：管理员应急入口）：仅 admin 可见——常规合同走主管审批流，
 *  此入口供「无主管 / 法大大未通」时管理员手动放行（有挂起审批单会一并闭合）。
 *  仅对 sent/customer_signed 合同显示，二次确认 → POST /contracts/:id/mark-signed → 刷新列表。 */
function ContractMarkSignedAction({ row }: { row: any }) {
  const qc = useQueryClient();
  const role = useAuthStore.getState().user?.role ?? '';
  const canMark = role === 'admin'; // 项3：收紧到 admin 应急（manager/director 走审批流）
  const markable = row.esignStatus === 'sent' || row.esignStatus === 'customer_signed';
  const mut = useMutation({
    mutationFn: () => $.post(`/contracts/${row.id}/mark-signed`, {}),
    onSuccess: () => { toast.success(`合同 ${row.contractCode} 已标记为已签`); qc.invalidateQueries({ queryKey: ['/contracts'] }); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e?.message ?? '标记失败'),
  });
  if (!canMark || !markable) return null;
  return (
    <button className="row-action" style={{ color: 'var(--primary)' }}
      onClick={(e) => {
        e.stopPropagation();
        Modal.confirm({
          title: '管理员应急·标记合同已签？',
          content: `确认把合同 ${row.contractCode} 标记为「全部签署」？常规合同应走主管审批流；此为应急放行（若有挂起审批单将一并通过），将解锁开发票 / 建订单 / 录收款。`,
          okText: '确认放行', cancelText: '取消',
          onOk: () => mut.mutateAsync(),
        });
      }}>
      ✓ 应急标记已签
    </button>
  );
}

/** P1-3b：软删按钮（合同/邮寄/资料）。仅持对应权限点(admin/director)显；二次确认 → DELETE → 刷新。 */
function BizSoftDeleteAction({ row, perm, endpoint, queryKey, title, content }: {
  row: any; perm: string; endpoint: string; queryKey: string; title: string; content: string;
}) {
  const qc = useQueryClient();
  const allowed = useHasPermission(perm);
  const mut = useMutation({
    mutationFn: () => $.delete(`${endpoint}/${row.id}`),
    onSuccess: () => { toast.success('已删除'); qc.invalidateQueries({ queryKey: [queryKey] }); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e?.message ?? '删除失败'),
  });
  if (!allowed) return null;
  return (
    <button className="row-action" style={{ color: 'var(--danger, #d4380d)' }}
      onClick={(e) => { e.stopPropagation(); Modal.confirm({ title, content, okText: '删除', okButtonProps: { danger: true }, cancelText: '取消', onOk: () => mut.mutateAsync() }); }}>
      删除
    </button>
  );
}

/** item1(教材邮寄录单号入口)：待发货单录快递公司+单号 → PATCH /shipments/:id/ship(后端已存在)。修"教材邮寄页加不了快递单号"。 */
function BizShipAction({ row }: { row: any }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [trackingNo, setTrackingNo] = useState('');
  const [courier, setCourier] = useState('');
  const mut = useMutation({
    mutationFn: () => $.patch(`/shipments/${row.id}/ship`, { trackingNo: trackingNo.trim(), courier: courier.trim() }),
    onSuccess: () => { toast.success('已发货'); setOpen(false); setTrackingNo(''); setCourier(''); qc.invalidateQueries({ queryKey: ['/shipments'] }); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e?.message ?? '发货失败'),
  });
  if (row.status !== 'pending') return null; // 仅"待发货"显录单号入口
  return (
    <>
      <button className="row-action" style={{ color: 'var(--primary, #1677ff)' }}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}>发货</button>
      <Modal title={`发货 · ${row.shipmentCode ?? ''}`} open={open}
        onCancel={() => setOpen(false)}
        onOk={() => { if (!courier.trim() || !trackingNo.trim()) { toast.error('快递公司和单号必填'); return; } mut.mutate(); }}
        okText={mut.isPending ? '提交中…' : '确认发货'} cancelText="取消" confirmLoading={mut.isPending}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Input placeholder="快递公司（如：顺丰 / 中通 / 圆通）" value={courier} onChange={(e) => setCourier(e.target.value)} />
          <Input placeholder="快递单号" value={trackingNo} onChange={(e) => setTrackingNo(e.target.value)} />
        </div>
      </Modal>
    </>
  );
}

/** spec1-B1①：学员资料「提交资料」。门控 material:create；仅 draft/in_progress/rejected 态显(submitted/approved 不再显防重复提交)。
 *  completionStatus≠complete 时先友好 toast 提示、不打后端(避免 400)；齐全则 Modal 二次确认 → POST /materials/:id/submit(空 body)。 */
function MaterialSubmitAction({ row }: { row: any }) {
  const qc = useQueryClient();
  const allowed = useHasPermission('material:create');
  const mut = useMutation({
    mutationFn: () => $.post(`/materials/${row.id}/submit`, {}),
    onSuccess: () => { toast.success('已提交，等待主管审核'); qc.invalidateQueries({ queryKey: ['/materials'] }); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e?.message ?? '提交失败'),
  });
  // submitStatus 真实值=新建即 'draft'(materials.module.ts:61)；'in_progress' 为兼容存量/UI 历史键，rejected 可重提。
  if (!allowed || !['draft', 'in_progress', 'rejected'].includes(row.submitStatus)) return null;
  return (
    <button className="row-action" disabled={mut.isPending}
      style={{ color: 'var(--primary, #1677ff)', ...(mut.isPending ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
      onClick={(e) => { e.stopPropagation();
        if (mut.isPending) return;
        // 完成度未满 100% 先友好提示、不打后端(后端 115 行仍会硬拦、此处提前拦体验更好)
        if (row.completionStatus !== 'complete') {
          toast.error(`资料未齐全或验真未通过，完成度 ${row.completionPercentage ?? 0}%，补齐后再提交`);
          return;
        }
        Modal.confirm({ title: '提交资料？', content: `确认提交资料 ${row.materialCode ?? ''} 给主管审核？`, okText: '提交', cancelText: '取消', onOk: () => mut.mutateAsync() });
      }}>
      {mut.isPending ? '提交中…' : '提交资料'}
    </button>
  );
}

/** spec1-B1②：学员资料「✓通过」。门控 material:approve(权限点·非角色白名单)；仅 submitted 态显。Modal 二次确认 → POST /materials/:id/approve。 */
function MaterialApproveAction({ row }: { row: any }) {
  const qc = useQueryClient();
  const allowed = useHasPermission('material:approve');
  const mut = useMutation({
    mutationFn: () => $.post(`/materials/${row.id}/approve`, {}),
    onSuccess: () => { toast.success('已通过'); qc.invalidateQueries({ queryKey: ['/materials'] }); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e?.message ?? '操作失败'),
  });
  if (!allowed || row.submitStatus !== 'submitted') return null;
  return (
    <button className="row-action" disabled={mut.isPending}
      style={{ color: 'var(--success, #389e0d)', ...(mut.isPending ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
      onClick={(e) => { e.stopPropagation();
        if (mut.isPending) return;
        Modal.confirm({ title: '通过资料？', content: `确认通过资料 ${row.materialCode ?? ''}？`, okText: '通过', cancelText: '取消', onOk: () => mut.mutateAsync() });
      }}>
      {mut.isPending ? '处理中…' : '✓通过'}
    </button>
  );
}

/** spec1-B1③：学员资料「✗驳回」。门控 material:approve；仅 submitted 态显。弹框 TextArea 必填理由 → POST /materials/:id/reject {reason}。 */
function MaterialRejectAction({ row }: { row: any }) {
  const qc = useQueryClient();
  const allowed = useHasPermission('material:approve');
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const mut = useMutation({
    mutationFn: () => $.post(`/materials/${row.id}/reject`, { reason: reason.trim() }),
    onSuccess: () => { toast.success('已驳回'); setOpen(false); setReason(''); qc.invalidateQueries({ queryKey: ['/materials'] }); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e?.message ?? '驳回失败'),
  });
  if (!allowed || row.submitStatus !== 'submitted') return null;
  return (
    <>
      <button className="row-action" style={{ color: 'var(--danger, #d4380d)' }}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}>✗驳回</button>
      <Modal title={`驳回资料 · ${row.materialCode ?? ''}`} open={open}
        onCancel={() => { setOpen(false); setReason(''); }}
        onOk={() => { if (!reason.trim()) { toast.error('请填写驳回理由'); return; } mut.mutate(); }}
        okText={mut.isPending ? '提交中…' : '确认驳回'} okButtonProps={{ danger: true }} cancelText="取消" confirmLoading={mut.isPending}>
        <Input.TextArea autoFocus value={reason} onChange={(e) => setReason(e.target.value)} rows={4}
          placeholder="请填写驳回理由（必填），如：身份证照片模糊、学历未验真等" />
      </Modal>
    </>
  );
}

/** P1-3b：作废按钮（收款/订单，红冲态留痕）。仅持对应权限点显；prompt 原因(可选) → POST :id/void → 刷新。 */
function BizVoidAction({ row, perm, endpoint, queryKey, label }: {
  row: any; perm: string; endpoint: string; queryKey: string; label: string;
}) {
  const qc = useQueryClient();
  const allowed = useHasPermission(perm);
  const mut = useMutation({
    mutationFn: (reason: string) => $.post(`${endpoint}/${row.id}/void`, { reason }),
    onSuccess: () => { toast.success('已作废'); qc.invalidateQueries({ queryKey: [queryKey] }); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e?.message ?? '作废失败'),
  });
  if (!allowed || row.status === 'void' || row.approvalStatus === 'voided') return null;
  return (
    // ux1 防重：作废进行中禁用按钮 + 文案切「作废中…」（window.prompt 阻塞后 mutate，仅需 disabled 防连点）
    <button className="row-action" disabled={mut.isPending}
      style={{ color: 'var(--danger, #d4380d)', ...(mut.isPending ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
      onClick={(e) => { e.stopPropagation();
        if (mut.isPending) return;
        const reason = window.prompt(`${label}原因（可选，留空亦可）：`);
        if (reason === null) return; // 取消
        mut.mutate(reason ?? '');
      }}>
      {mut.isPending ? '作废中…' : '作废'}
    </button>
  );
}

/** P1-3b：发票操作——已开走作废(红冲 void)、未开走软删(delete)。仅 invoice:void 持有者显。 */
function InvoiceDangerAction({ row }: { row: any }) {
  const qc = useQueryClient();
  const allowed = useHasPermission('invoice:void');
  const opened = ['issued', 'approved'].includes(row.status)
    || ['opened', 'sent_to_customer', 'confirmed'].includes(row.requestStatus ?? '');
  const voidMut = useMutation({
    mutationFn: (reason: string) => $.post(`/invoices/${row.id}/void`, { reason }),
    onSuccess: () => { toast.success('发票已作废'); qc.invalidateQueries({ queryKey: ['/invoices'] }); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e?.message ?? '作废失败'),
  });
  const delMut = useMutation({
    mutationFn: () => $.delete(`/invoices/${row.id}`),
    onSuccess: () => { toast.success('发票已删除'); qc.invalidateQueries({ queryKey: ['/invoices'] }); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e?.message ?? '删除失败'),
  });
  if (!allowed || row.status === 'void') return null;
  if (opened) {
    return (
      // ux1 防重：作废(红冲)进行中禁用 + 文案切「作废中…」（删除分支走 Modal.confirm mutateAsync 已隐式防护，不动）
      <button className="row-action" disabled={voidMut.isPending}
        style={{ color: 'var(--danger, #d4380d)', ...(voidMut.isPending ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
        onClick={(e) => { e.stopPropagation();
          if (voidMut.isPending) return;
          const reason = window.prompt('发票作废(红冲)原因（可选）：');
          if (reason === null) return;
          voidMut.mutate(reason ?? '');
        }}>{voidMut.isPending ? '作废中…' : '作废'}</button>
    );
  }
  return (
    <button className="row-action" style={{ color: 'var(--danger, #d4380d)' }}
      onClick={(e) => { e.stopPropagation(); Modal.confirm({ title: '删除未开发票？', content: `确认删除发票 ${row.invoiceCode}？（未开具发票软删，可恢复）`, okText: '删除', okButtonProps: { danger: true }, cancelText: '取消', onOk: () => delMut.mutateAsync() }); }}>删除</button>
  );
}

export function ContractsListPage() {
  return (
    <BizListPage cfg={{
      endpoint: '/contracts',
      breadcrumb: ['客户流程', '签订合同'],
      pageTitle: '签订合同', pageTag: '第 3 步', pageSubtitle: '从意向到成单的关键节点',
      // 即将到期（serviceEndAt 在 30 天内）：浅红背景提醒续约
      rowClassName: (r) => {
        if (!r.serviceEndAt) return undefined;
        const d = dayjs(r.serviceEndAt).diff(dayjs(), 'day');
        return (d >= 0 && d < 30) ? 'expire-soon' : undefined;
      },
      // Backend contracts.list() filters esignStatus via ?status=... — real enum values
      // are draft / sent / customer_signed / all_signed. "服务中/即将到期"等按
      // serviceEndAt 日期范围过滤后端未支持。
      filterTabs: [
        { key: 'all', label: '全部' },
        { key: 'all_signed', label: '已签订', param: { status: 'all_signed' } },
        { key: 'sent', label: '待签字', param: { status: 'sent' } },
        { key: 'draft', label: '草稿', param: { status: 'draft' } },
      ],
      stats: (items, total) => {
        const amount = items.reduce((s, r) => s + Number(r.amount ?? 0), 0);
        return [
          { label: '合同总数', value: total, unit: '份' },
          { label: '本月新签', value: items.filter((r) => dayjs(r.signedAt).isAfter(dayjs().startOf('month'))).length, unit: '份', valueColor: 'success' },
          { label: '总金额', value: `¥${(amount / 10000).toFixed(1)}万`, meta: '当前页合计' },
          { label: '即将到期', value: items.filter((r) => r.serviceEndAt && dayjs(r.serviceEndAt).diff(dayjs(), 'day') < 30).length, unit: '份', valueColor: 'warning' },
        ];
      },
      columns: [
        { title: '客户', width: '16%', render: (r, _idx, all) => {
          // Detect renewal: same customer with another contract created earlier on the page.
          const isRenewal = !!all?.some((other) =>
            other.id !== r.id && other.customerId === r.customerId &&
            dayjs(other.createdAt).isBefore(dayjs(r.createdAt)),
          );
          return (
            <div className="cust-cell">
              <div className={`cust-avatar c${avatarSlot(r.customerId ?? r.id)}`}>
                {(r.customerName ?? '?').charAt(0)}
              </div>
              <div className="cust-info">
                <div className="cust-name">
                  {r.customerName ?? `客户 #${r.customerId}`}
                  {isRenewal && (
                    <span
                      className="cust-name-flag flag-b"
                      data-tip="该客户存在历史合同（续约客户）"
                    >续约</span>
                  )}
                </div>
                <div className="cust-meta">合同 {r.contractCode}</div>
              </div>
            </div>
          );
        } },
        { title: '合同编号', width: '12%', render: (r) => <><div className="time-text">{r.contractCode}</div>{r.allSignedAt && <div className="time-sub">{date(r.allSignedAt)} 签订</div>}</> },
        { title: '购买服务', width: '12%', render: (r) => <Pill tone="default">{r.serviceType ?? '—'}</Pill> },
        { title: '跨境网络', width: '12%', render: (r) => <span className="time-text">{r.servicePackage ?? '—'}</span> },
        { title: '服务期间', width: '18%', render: (r) => <><div className="time-text">{date(r.serviceStartAt)} — {date(r.serviceEndAt)}</div>{r.serviceEndAt && <div className="time-sub">还剩 {Math.max(0, dayjs(r.serviceEndAt).diff(dayjs(), 'day'))} 天</div>}</> },
        { title: '金额', width: '10%', render: (r) => moneyBig(r.amount) },
        { title: '状态', width: '10%', render: (r) => statusPill(r.esignStatus ?? 'draft', CONTRACT_STATUS) },
        { title: '操作', width: '12%', align: 'right', render: (r) => (
          <div className="row-actions" style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
            <ContractMarkSignedAction row={r} />
            <button className="row-action">查看</button>
            <BizSoftDeleteAction row={r} perm="contract:delete" endpoint="/contracts" queryKey="/contracts"
              title="删除合同？" content="名下有已确认收款/已通过订单则不可删除；软删可恢复。仅管理员/总监可删。" />
          </div>
        ) },
      ],
    }} />
  );
}

/* ─── S4: 发票 ──────────────────────────────────────────── */
// 追加A①：补全发票枚举中文化——覆盖 status(draft/submitted/pending_open/opened/confirmed/issued/approved/revoked)
//   + requestStatus(pending_open/opened/sent_to_customer/confirmed/rejected)，避免 statusPill 回退显英文原值。
const INVOICE_STATUS: Record<string, { tone: PillTone; label: string }> = {
  pending: { tone: 'warning', label: '待开票' },
  draft: { tone: 'default', label: '草稿' },
  submitted: { tone: 'primary', label: '待开票' },
  pending_open: { tone: 'warning', label: '待开票' },
  opened: { tone: 'primary', label: '已开票·待发客户' },
  sent_to_customer: { tone: 'primary', label: '已发客户·待确认' },
  confirmed: { tone: 'success', label: '客户已确认' },
  issued: { tone: 'success', label: '已开票' },
  approved: { tone: 'success', label: '已通过' },
  rejected: { tone: 'danger', label: '已驳回' },
  revoked: { tone: 'danger', label: '已作废' },
  void: { tone: 'danger', label: '已作废' }, // 存量bug2：作废发票实际 status='void'(非 revoked)，补此项免 statusPill 回退显英文
};
export function InvoicesListPage() {
  // 存量bug2(方案C)：「作废」tab 可见性用【权限点】控制(invoice:void=有权作废的人才看作废单)——
  //   ★绝不用 allowedRoles 角色白名单(bug9 根因，权限批已治掉，新功能不再引入)★。
  const canViewVoid = useHasPermission('invoice:void');
  return (
    <BizListPage cfg={{
      endpoint: '/invoices',
      breadcrumb: ['客户流程', '发票管理'],
      pageTitle: '发票管理', pageTag: '第 5 步', pageSubtitle: '收款后开票（开了就走）',
      filterTabs: [
        { key: 'all', label: '全部' },
        // "待开票" = 后端 status IN (draft, submitted) — 用 ?pending=1 虚拟过滤
        { key: 'pending', label: '待开票', param: { pending: '1' } },
        { key: 'issued', label: '已开票', param: { issued: '1' } },
        // 默认列表后端已排作废(status<>'void')；此 tab 显式查作废，仅 invoice:void 权限点可见
        ...(canViewVoid ? [{ key: 'void', label: '作废', param: { status: 'void' } }] : []),
      ],
      stats: (items, total) => {
        const amount = items.reduce((s, r) => s + Number(r.amount ?? 0), 0);
        const pendingCount = items.filter((r) => r.status === 'draft' || r.status === 'submitted').length;
        return [
          { label: '发票总数', value: total, unit: '张' },
          { label: '待开票', value: pendingCount, unit: '张', valueColor: 'warning' },
          // P2-10 修：原认从不写入的 'issued' → 恒 0。改双字段兜底（抄财务工作台 financeDashboard）
          { label: '已开票', value: items.filter((r) => ['issued', 'approved'].includes(r.status) || ['opened', 'sent_to_customer', 'confirmed'].includes(r.requestStatus)).length, unit: '张', valueColor: 'success' },
          { label: '总金额', value: `¥${(amount / 10000).toFixed(1)}万`, meta: '当前页合计' },
        ];
      },
      columns: [
        { title: '客户', width: '16%', render: (r) => <CustCell
          id={r.customerId}
          name={r.customerName ?? `客户 #${r.customerId}`}
          meta={r.contractCode ?? (r.contractId ? `合同 #${r.contractId}` : '')}
        /> },
        { title: '发票编号', width: '14%', render: (r) => <><div className="time-text">{r.invoiceCode}</div>{r.issuedAt && <div className="time-sub">{date(r.issuedAt)} 开</div>}</> },
        { title: '类型', width: '10%', render: (r) => {
          // ux11：文本走 invTypeLabel 单一真相源；tone(invoice=primary 其余 default)+none 的 tooltip 保留
          const label = invTypeLabel(r.type);
          if (r.type === 'none') return (
            <span data-tip="客户不需要发票或收据（签订合同时已勾选）" style={{ display: 'inline-block' }}>
              <Pill tone="default">{label}</Pill>
            </span>
          );
          return <Pill tone={r.type === 'invoice' ? 'primary' : 'default'}>{label}</Pill>;
        } },
        { title: '金额', width: '12%', render: (r) => moneyBig(r.amount) },
        { title: '发票抬头', width: '22%', render: (r) => <><div className="time-text">{r.headerName ?? '—'}</div><div className="time-sub">{r.headerType === 'company' ? '单位抬头' : '个人抬头'}</div></> },
        { title: '开票日期', width: '10%', render: (r) => <span className="time-text">{r.issuedAt ? dateShort(r.issuedAt) : '—'}</span> },
        { title: '状态', width: '10%', render: (r) => statusPill(r.status, INVOICE_STATUS) },
        { title: '操作', width: '8%', align: 'right', render: (r) => <div className="row-actions" style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}><button className="row-action">查看</button><InvoiceDangerAction row={r} /></div> },
      ],
    }} />
  );
}

/* ─── S5: 收款 ──────────────────────────────────────────── */
// Status pill is computed from amounts, NOT the backend status field — the latter
// has been wrong before (陈默 partial 收款被标 completed). We use the canonical:
//   paid >= due  → 已完成
//   paid > 0     → 部分收款
//   overdue past → 已逾期
//   else         → 待收款
const PAYMENT_STATUS: Record<string, { tone: PillTone; label: string }> = {
  pending: { tone: 'warning', label: '待收款' },
  partial: { tone: 'primary', label: '部分收款' },
  completed: { tone: 'success', label: '已完成' },
  overdue: { tone: 'danger', label: '已逾期' },
  void: { tone: 'danger', label: '已作废' }, // 存量bug2：作废收款 status='void'
};
function paymentStatusFromAmounts(r: any): { tone: PillTone; label: string } {
  const due = Number(r.totalDue ?? 0);
  const paid = Number(r.totalPaid ?? 0);
  const balance = Number(r.balanceDue ?? Math.max(0, due - paid));
  const overdueDays = Number(r.overdueDays ?? 0);
  if (due > 0 && paid >= due) return PAYMENT_STATUS.completed;
  if (overdueDays > 0 && balance > 0) return PAYMENT_STATUS.overdue;
  if (paid > 0 && paid < due) return PAYMENT_STATUS.partial;
  return PAYMENT_STATUS.pending;
}
export function PaymentsListPage() {
  // 存量bug2(方案C)：「作废」tab 用权限点 payment:void 控制(非角色白名单)
  const canViewVoid = useHasPermission('payment:void');
  // v1.5 C-1.b #2：录入回款 modal state
  const [recordingPayment, setRecordingPayment] = useState<any | null>(null);
  // v1.5 批1 #2：按 role 显示按钮 — sales(owner 自己) / manager / director / admin 可录款；academic 不可
  const me = useAuthStore.getState().user;
  const canRecordPayment = (paymentRow: any): boolean => {
    if (!me) return false;
    if (me.role === 'admin' || me.role === 'director') return true;
    if (me.role === 'manager') return true; // 团队范围由后端 ownership 校验
    if (me.role === 'sales') {
      // sales 仅自己持有的（payments.ownerId === me.id）能录款
      return Number(paymentRow.ownerId) === Number(me.id);
    }
    return false;
  };
  return (
    <>
    <BizListPage cfg={{
      endpoint: '/payments',
      breadcrumb: ['客户流程', '收款管理'],
      pageTitle: '收款', pageTag: '第 4 步', pageSubtitle: '全款一次性 / 定金+尾款追踪',
      filterTabs: [
        { key: 'all', label: '全部' },
        { key: 'pending', label: '待收款', param: { status: 'pending' } },
        { key: 'partial', label: '部分收款', param: { status: 'partial' } },
        { key: 'overdue', label: '已逾期', param: { status: 'overdue' } },
        { key: 'completed', label: '已完成', param: { status: 'completed' } },
        // 默认列表后端已排作废(status<>'void')；此 tab 显式查作废，仅 payment:void 权限点可见
        ...(canViewVoid ? [{ key: 'void', label: '作废', param: { status: 'void' } }] : []),
      ],
      stats: (items, total) => {
        const due = items.reduce((s, r) => s + Number(r.balanceDue ?? 0), 0);
        const overdue = items.filter((r) => r.status === 'overdue').length;
        // 待收金额必须精确显示（演示时财务要看具体到元的余款），不是 "¥1.5万" 这种近似
        const dueText = `¥${Math.round(due).toLocaleString('zh-CN')}`;
        return [
          { label: '收款单数', value: total, unit: '笔' },
          { label: '待收金额', value: dueText, valueColor: 'warning' },
          { label: '逾期单数', value: overdue, unit: '笔', valueColor: 'danger' },
          { label: '本月完成', value: items.filter((r) => r.status === 'completed' && dayjs(r.completedAt).isAfter(dayjs().startOf('month'))).length, unit: '笔', valueColor: 'success' },
        ];
      },
      columns: [
        { title: '客户', width: '16%', render: (r) => <CustCell id={r.customerId} name={r.customerName ?? `客户 #${r.customerId}`} meta={`合同 ${r.contractCode ?? '—'}`} /> },
        { title: '订单类型', width: '10%', render: (r) => <span className="time-text">{r.orderType === 'full' ? '全款订单' : '定金订单'}</span> },
        { title: '应收金额', width: '12%', render: (r) => moneyBig(r.totalDue) },
        { title: '已收', width: '14%', render: (r) => moneyStack({
          value: r.totalPaid,
          sub: r.totalPaid > 0 ? `${r.orderType === 'full' ? '全款' : '定金'}${r.paidAt ? ' · ' + dateShort(r.paidAt) : ''}` : undefined,
          tone: 'success',
        }) },
        { title: '待收', width: '12%', render: (r) => moneyStack({
          value: r.balanceDue,
          sub: r.balanceDue > 0 ? '尾款' : undefined,
          tone: r.status === 'overdue' ? 'danger' : 'warning',
        }) },
        { title: '缴款时间', width: '12%', render: (r) => {
          // v1.5 C-1.a #7 修：列字段映射错（'已完成'本是状态语义，串到时间列）
          // 三态展示：① 有 dueAt → 显示日期 + 逾期/待支付 副标
          //          ② 已收齐（totalPaid >= totalDue）→ 显示"已结清"(状态，不显示时间这里)
          //          ③ 都没有 → "—"
          if (r.dueAt) {
            return <><div className={`time-text ${r.overdueDays > 0 ? 'danger' : ''}`}>{dateShort(r.dueAt)}</div><div className="time-sub">{r.overdueDays > 0 ? `已逾期 ${r.overdueDays} 天` : '待支付'}</div></>;
          }
          if (Number(r.totalPaid ?? 0) >= Number(r.totalDue ?? 0) && Number(r.totalDue ?? 0) > 0) {
            return <div className="time-text muted">已结清</div>;
          }
          return <div className="time-text muted">—</div>;
        } },
        { title: '状态', width: '10%', render: (r) => {
          // 存量bug2：作废收款优先显"已作废"(否则 paymentStatusFromAmounts 按金额会误标待收/完成)
          const m = r.status === 'void' ? PAYMENT_STATUS.void : paymentStatusFromAmounts(r);
          return <Pill tone={m.tone}>{m.label}</Pill>;
        } },
        { title: '操作', width: '14%', align: 'right', render: (r) => {
          // v1.5 C-1.b #2 + 批1：未结清 + 当前用户有权时显示"录入回款"
          // 权限：admin/director/manager 全部能录；sales 仅自己持有的（payment.ownerId 必须=actor.id）
          const owing = Number(r.balanceDue ?? 0) > 0 && r.status !== 'completed';
          const showRecord = owing && canRecordPayment(r);
          return (
            <div className="row-actions" style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
              {showRecord && (
                <button className="row-action" style={{ color: 'var(--primary)' }}
                        onClick={(e) => { e.stopPropagation(); setRecordingPayment(r); }}>
                  💵 录入回款
                </button>
              )}
              <button className="row-action">查看</button>
              <BizVoidAction row={r} perm="payment:void" endpoint="/payments" queryKey="/payments" label="作废收款单" />
            </div>
          );
        } },
      ],
    }} />
    {/* v1.5 C-1.b #2 财务闭环：录入回款弹窗 */}
    <PaymentRecordModal open={!!recordingPayment} payment={recordingPayment}
                        onClose={() => setRecordingPayment(null)} />
    </>
  );
}

/* ─── S6: 邮寄 ──────────────────────────────────────────── */
const SHIPMENT_STATUS: Record<string, { tone: PillTone; label: string }> = {
  pending: { tone: 'warning', label: '待发货' },
  shipped: { tone: 'primary', label: '已发货' },
  in_transit: { tone: 'primary', label: '运输中' },
  delivered: { tone: 'success', label: '已签收' },
  exception: { tone: 'danger', label: '异常' },
};
/** 项6：邮寄列表「+新建邮寄单」选客户弹窗。
 *  只列【当前用户 scope 内 且 sales_flow_step==='6_shipment'】的客户(scope 由后端 applyCustomerScope 兜底·此处只传过滤参数)。
 *  选中 customerId → 复用客户详情页的 ShipmentCreateModal。 */
function ShipmentCreatePicker({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [customerId, setCustomerId] = useState<number | undefined>(undefined);
  // 已选客户 → 关掉选客户弹窗、打开建单弹窗（建单弹窗 onClose 回收两层状态）
  const { data, isLoading } = useQuery<{ items: any[] }>({
    queryKey: ['/customers', 'shipment-picker'],
    // pageSize 拉满(≤200)：邮寄步客户通常少量，scope 内一次性取全、前端 Select 搜索即可
    queryFn: () => $.get('/customers', { salesFlowStep: '6_shipment', pageSize: 200 }),
    enabled: open,
    staleTime: 30_000,
  });
  const options = (data?.items ?? []).map((c: any) => ({
    value: Number(c.id),
    label: `${c.realName || c.nickname || `客户 #${c.id}`}${c.internalCode ? `（${c.internalCode}）` : ''}`,
  }));
  return (
    <>
      <Modal title="新建邮寄单 · 选择客户" open={open && customerId === undefined}
        onCancel={onClose} footer={null} width={480}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ color: 'var(--text-3)', fontSize: 13 }}>仅显示进入「教材邮寄」步骤(6_shipment)且你有权限的客户</div>
          <Select showSearch placeholder={isLoading ? '加载中…' : '搜索并选择客户'}
            style={{ width: '100%' }} loading={isLoading} options={options}
            optionFilterProp="label"
            notFoundContent={isLoading ? '加载中…' : '当前没有处于教材邮寄步骤的客户'}
            onChange={(v) => setCustomerId(Number(v))} />
        </div>
      </Modal>
      {customerId !== undefined && (
        // ShipmentCreateModal 内部成功只 invalidate ['customer',id]，此处关闭时补刷邮寄列表(取消/成功均无害)
        <ShipmentCreateModal open={open} customerId={customerId}
          onClose={() => { setCustomerId(undefined); qc.invalidateQueries({ queryKey: ['/shipments'] }); onClose(); }} />
      )}
    </>
  );
}

export function ShipmentsListPage() {
  const [pickerOpen, setPickerOpen] = useState(false);
  return (
    <>
    <BizListPage cfg={{
      endpoint: '/shipments',
      breadcrumb: ['客户流程', '教材邮寄'],
      pageTitle: '教材邮寄', pageTag: '第 6 步', pageSubtitle: '发货 + 物流追踪',
      primaryAction: { label: '+ 新建邮寄单', onClick: () => setPickerOpen(true) },
      filterTabs: [
        { key: 'all', label: '全部' },
        { key: 'pending', label: '待发货', param: { status: 'pending' } },
        { key: 'in_transit', label: '运输中', param: { status: 'in_transit' } },
        { key: 'delivered', label: '已签收', param: { status: 'delivered' } },
      ],
      stats: (items, total) => [
        { label: '邮寄总数', value: total, unit: '票' },
        { label: '待发货', value: items.filter((r) => r.status === 'pending').length, unit: '票', valueColor: 'warning' },
        { label: '运输中', value: items.filter((r) => r.status === 'in_transit' || r.status === 'shipped').length, unit: '票' },
        { label: '已签收', value: items.filter((r) => r.status === 'delivered').length, unit: '票', valueColor: 'success' },
      ],
      columns: [
        { title: '客户', width: '14%', render: (r) => <CustCell id={r.customerId} name={r.customerName ?? `客户 #${r.customerId}`} meta={r.shippedAt ? `${dateShort(r.shippedAt)} 寄出` : '关联收款'} /> },
        { title: '邮寄编号', width: '14%', render: (r) => <><div className="time-text">{r.shipmentCode}</div>{r.shippedAt && <div className="time-sub">{dateShort(r.shippedAt)} 寄出</div>}</> },
        { title: '邮寄材料', width: '14%', render: (r) => {
          const mats = Array.isArray(r.materials) ? r.materials : [];
          return <><div className="time-text">{mats.length > 0 ? mats.join('+') : '教材+教案+合同'}</div><div className="time-sub">{mats.length > 0 ? `${mats.length} 件` : '默认套餐'}</div></>;
        } },
        { title: '邮寄地址', width: '18%', render: (r) => <><div className="time-text" style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.address ?? '—'}</div>{r.recipientPhone && <div className="time-sub">{r.recipientPhone}</div>}</> },
        { title: '快递单号', width: '14%', render: (r) => r.trackingNo ? <><div className="time-text">{r.trackingNo}</div>{r.courier && <div className="time-sub">{r.courier}</div>}</> : <div className="time-text muted">未发货</div> },
        { title: '快递状态', width: '12%', render: (r) => statusPill(r.status, SHIPMENT_STATUS) },
        { title: '操作', width: '10%', align: 'right', render: (r) => <div className="row-actions" style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}><BizShipAction row={r} /><button className="row-action">查看</button><BizSoftDeleteAction row={r} perm="shipment:delete" endpoint="/shipments" queryKey="/shipments" title="删除教材邮寄单？" content="软删，可恢复。仅管理员/总监可删。" /></div> },
      ],
    }} />
    <ShipmentCreatePicker open={pickerOpen} onClose={() => setPickerOpen(false)} />
    </>
  );
}

/* ─── S7: 学员资料 ──────────────────────────────────────── */
// 追加A②：补 'draft'（student_material.submit_status 默认值 draft，新建即此态，原表缺键显英文）
const MATERIAL_STATUS: Record<string, { tone: PillTone; label: string }> = {
  draft: { tone: 'default', label: '草稿' },
  in_progress: { tone: 'warning', label: '收集中' },
  submitted: { tone: 'primary', label: '已提交' },
  approved: { tone: 'success', label: '已通过' },
  rejected: { tone: 'danger', label: '已拒绝' },
};
export function MaterialsListPage() {
  return (
    <BizListPage cfg={{
      endpoint: '/materials',
      breadcrumb: ['客户流程', '学员资料'],
      pageTitle: '学员资料', pageTag: '第 7 步', pageSubtitle: '身份证 / 学历 / 二寸照',
      filterTabs: [
        { key: 'all', label: '全部' },
        { key: 'in_progress', label: '收集中', param: { status: 'in_progress' } },
        { key: 'submitted', label: '已提交', param: { status: 'submitted' } },
        { key: 'approved', label: '已通过', param: { status: 'approved' } },
        { key: 'rejected', label: '已驳回', param: { status: 'rejected' } },
      ],
      stats: (items, total) => {
        const avgPct = items.length ? Math.round(items.reduce((s, r) => s + Number(r.completionPercentage ?? 0), 0) / items.length) : 0;
        return [
          { label: '资料档案', value: total, unit: '份' },
          { label: '平均完成度', value: `${avgPct}%` },
          { label: '已提交', value: items.filter((r) => r.submitStatus === 'submitted' || r.submitStatus === 'approved').length, unit: '份', valueColor: 'success' },
          { label: '已驳回', value: items.filter((r) => r.submitStatus === 'rejected').length, unit: '份', valueColor: 'danger' },
        ];
      },
      columns: [
        { title: '客户', width: '16%', render: (r) => <CustCell id={r.customerId} name={r.customerName ?? `客户 #${r.customerId}`} meta={`资料 ${r.materialCode}`} /> },
        { title: '完成度', width: '12%', render: (r) => (
          <div className="tbl-progress">
            <div className="tbl-progress-bar"><div className="tbl-progress-fill" style={{ width: `${r.completionPercentage}%`, background: r.completionPercentage >= 70 ? 'var(--success)' : r.completionPercentage >= 40 ? 'var(--warning)' : 'var(--danger)' }} /></div>
            <span className="tbl-progress-val">{r.completionPercentage}%</span>
          </div>
        ) },
        { title: '完成度检查', width: '32%', render: (r) => (
          <div className="checks">
            <CheckPip label="身份证" missing={!r.idCardVerified} />
            <CheckPip label="学历" missing={!r.educationVerified} />
            <CheckPip label="二寸照" missing={!r.photoVerified} />
            <CheckPip label="AI 验真" missing={!r.aiVerified && !(r.idCardVerified && r.educationVerified && r.photoVerified)} />
          </div>
        ) },
        { title: '创建时间', width: '12%', render: (r) => <span className="time-text">{dateShort(r.createdAt)}</span> },
        { title: '审批状态', width: '14%', render: (r) => statusPill(r.submitStatus, MATERIAL_STATUS) },
        { title: '操作', width: '18%', align: 'right', render: (r) => <div className="row-actions" style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}><button className="row-action">查看</button><MaterialSubmitAction row={r} /><MaterialApproveAction row={r} /><MaterialRejectAction row={r} /><BizSoftDeleteAction row={r} perm="material:delete" endpoint="/materials" queryKey="/materials" title="删除学员资料？" content="软删，可恢复。仅管理员/总监可删。" /></div> },
      ],
    }} />
  );
}

/* ─── S8: 订单 ──────────────────────────────────────────── */
// 追加A③：补 'none'(approval_status 默认值，未送审) + 'pending'(审批中)——原表缺键显英文
const ORDER_STATUS: Record<string, { tone: PillTone; label: string }> = {
  none: { tone: 'default', label: '未送审' },
  pending: { tone: 'warning', label: '审批中' },
  draft: { tone: 'default', label: '草稿' },
  submitted: { tone: 'primary', label: '已提交' },
  approved: { tone: 'success', label: '已批准' },
  rejected: { tone: 'danger', label: '已拒绝' },
  cancelled: { tone: 'default', label: '已取消' },
};
export function OrdersListPage() {
  return (
    <BizListPage cfg={{
      endpoint: '/orders',
      breadcrumb: ['客户流程', '提交订单'],
      pageTitle: '提交订单', pageTag: '第 8 步', pageSubtitle: '最后一公里：申请提成',
      filterTabs: [
        { key: 'all', label: '全部' },
        { key: 'submitted', label: '待审批', param: { status: 'pending' } }, // batch-fix#H26：订单建单写 approval_status='pending'(非 submitted)
        { key: 'approved', label: '已批准', param: { status: 'approved' } },
        { key: 'rejected', label: '已拒绝', param: { status: 'rejected' } },
      ],
      stats: (items, total) => {
        const amount = items.reduce((s, r) => s + Number(r.amount ?? 0), 0);
        const commission = items.reduce((s, r) => s + Number(r.commission ?? 0), 0);
        return [
          { label: '订单总数', value: total, unit: '单' },
          { label: '总金额', value: `¥${(amount / 10000).toFixed(1)}万` },
          { label: '提成合计', value: `¥${Math.round(commission).toLocaleString()}`, valueColor: 'success' },
          { label: '待审批', value: items.filter((r) => r.approvalStatus === 'pending').length, unit: '单', valueColor: 'warning' },
        ];
      },
      columns: [
        { title: '客户', width: '18%', render: (r) => <CustCell id={r.customerId} name={r.customerName ?? `客户 #${r.customerId}`} meta={`订单 ${r.orderCode}${r.dealAt ? ' · ' + dateShort(r.dealAt) + ' 成单' : ''}`} /> },
        { title: '订单金额', width: '12%', render: (r) => moneyBig(r.amount) },
        { title: '报名班型', width: '12%', render: (r) => <Pill tone="default">{r.classType ?? '—'}</Pill> },
        { title: '完成度检查', width: '24%', render: (r) => (
          r.approvalStatus === 'pending' || r.approvalStatus === 'approved'
            ? <ApprovalFlow status={r.approvalStatus} />
            : (
              <div className="checks">
                <CheckPip label="身份证" missing={!r.idCardOk} />
                <CheckPip label="学历" missing={!r.educationOk} />
                <CheckPip label="二寸照" missing={!r.photoOk} />
                <CheckPip label="收款" missing={!r.paymentOk} />
              </div>
            )
        ) },
        { title: '提成', width: '10%', render: (r) => money(r.commission) },
        { title: '审批状态', width: '14%', render: (r) => statusPill(r.approvalStatus, ORDER_STATUS) },
        { title: '操作', width: '12%', align: 'right', render: (r) => <div className="row-actions" style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}><button className="row-action">查看</button><BizVoidAction row={r} perm="order:void" endpoint="/orders" queryKey="/orders" label="作废订单" /></div> },
      ],
    }} />
  );
}
